var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import BigNumber from 'bignumber.js';
import { isWalletStandardError, WALLET_STANDARD_ERROR__USER__REQUEST_REJECTED, } from '@wallet-standard/errors';
import { getIcon } from './getIcon';
import { getSuiClient } from './client';
import { getTxErrorMessage } from './txResult';
import { AlphalendClient } from 'alphalend-sdk';
import { getSuiGraphqlUrl, getSuiGrpcOptions } from './rpcEndpoints';
import { marketsArray } from '../data/marketInfo';
import { getMoveErrorMessage, getRawErrorMessage, logTransactionError } from './errorMaps';
import { dryRunAndReport, dryRunDebug } from './dryRunDebug';
const MIN_SUI_BALANCE = 0.1;
const SUI_DECIMALS = 9;
/**
 * Gas budget for these transactions, in MIST. 0.05 SUI — ~2.4x the heaviest cost measured
 * on the AlphaLend path (21.2M MIST; median 9.1M). Pinned because the wallet's auto-estimate
 * has come in under the real cost and reverted transactions on-chain with InsufficientGas,
 * which the dry run can't catch (see dryRun.ts isGasError). Deliberately not larger: the
 * budget is reserved out of the gas coin, and SUI inputs come from that same coin, so an
 * oversized budget breaks near-max SUI supplies. See transactionHandlers.ts for the full note.
 */
const GAS_BUDGET_MIST = 50000000;
const EXPLORER_BASE_URL = 'https://suiscan.xyz';
const SWAP_TOKEN_TO_COIN_TYPE = {
    USDC: '0xdba34672e30cb065b1f93e3ab55318768fd6fef66c15942c9f7cb846e2f900e7::usdc::USDC',
    suiUSDT: '0x375f70cf2ae4c00bf37117d0c85a2c71545e6ee05c4a5c7d282cd66a4504b068::usdt::USDT',
    stSUI: '0xd1b72982e40348d069bb1ff701e634c117bb5f741f44dff91e472d3b01461e55::stsui::STSUI',
    SUI: '0x2::sui::SUI',
};
const ACTION_DISPLAY_NAMES = {
    Supply: 'Supply',
    Withdraw: 'Withdraw',
    Borrow: 'Borrow',
    Repay: 'Repay',
    swap_supply: 'Swap & Supply',
    swap_repay: 'Swap & Repay',
};
const dispatchTransactionEvent = (txType, success, analyticsPayload) => {
    document.dispatchEvent(new CustomEvent(success ? 'transactionSuccess' : 'transactionError', {
        detail: Object.assign({ txType }, analyticsPayload),
    }));
};
/** Walk `Error.cause` chain (limited depth) for nested wallet / RPC errors. */
function flattenErrorChain(error) {
    const chain = [];
    let current = error;
    for (let i = 0; i < 8 && current != null; i++) {
        chain.push(current);
        if (current instanceof Error && current.cause !== undefined) {
            current = current.cause;
        }
        else {
            break;
        }
    }
    return chain;
}
function combinedErrorText(error) {
    return flattenErrorChain(error)
        .map((e) => (e instanceof Error ? e.message : typeof e === 'string' ? e : ''))
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
}
/**
 * User dismissed the wallet prompt vs execution/simulation failure.
 * 1) Wallet Standard `USER__REQUEST_REJECTED` (see @wallet-standard/errors)
 * 2) EIP-1193-style `code === 4001` on any error in the chain
 * 3) Message heuristics for wallets that only set plain `Error.message`
 */
function isUserRejectionError(error) {
    for (const e of flattenErrorChain(error)) {
        if (isWalletStandardError(e, WALLET_STANDARD_ERROR__USER__REQUEST_REJECTED)) {
            return true;
        }
        if (typeof e === 'object' && e !== null && 'code' in e) {
            const c = e.code;
            const n = typeof c === 'number' ? c : typeof c === 'string' ? Number(c) : NaN;
            if (n === 4001) {
                return true;
            }
        }
    }
    const lower = combinedErrorText(error);
    return (lower.includes('rejected') ||
        lower.includes('cancelled') ||
        lower.includes('user denied') ||
        lower.includes('denied the request'));
}
const createExplorerLink = (network, digest) => {
    if (!digest)
        return '';
    return `${EXPLORER_BASE_URL}/${network === 'testnet' ? 'testnet' : 'mainnet'}/tx/${digest}`;
};
const createClients = (network) => {
    const suiClient = getSuiClient(network || 'mainnet');
    return {
        suiClient,
        alphalendClient: new AlphalendClient(network || 'mainnet', getSuiGraphqlUrl(network || 'mainnet'), getSuiGrpcOptions(network || 'mainnet')),
    };
};
const hasInsufficientBalance = (suiBalance, orEqual = false) => {
    const balance = new BigNumber(suiBalance).dividedBy(new BigNumber(10).pow(SUI_DECIMALS));
    return orEqual ? balance.lte(MIN_SUI_BALANCE) : balance.lt(MIN_SUI_BALANCE);
};
const formatSmallNumber = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue >= 0.0001)
        return value;
    if (numValue > 0) {
        const subscriptDigits = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
        // Zeros between the decimal point and the first significant digit, read off the exact
        // exponent. |floor(log10(3e-8))| is 8, but 0.00000003 has seven leading zeros — the old
        // arithmetic used that 8 as the subscript and rendered every dust amount ten times too
        // small. Digits come from the mantissa and are truncated, not rounded, so the subscript
        // and the digits can never disagree about the magnitude (rounding 9.99e-7 up to 1.0e-6
        // moves the first significant digit into a different decimal place).
        const [mantissa, exponent] = numValue.toExponential().split('e');
        const leadingZeros = -Number(exponent) - 1;
        const significantDigits = mantissa.replace('.', '').slice(0, 2).replace(/0+$/, '');
        const subscriptPos = leadingZeros
            .toString()
            .split('')
            .map((d) => subscriptDigits[parseInt(d)])
            .join('');
        return `0.0${subscriptPos}${significantDigits}`;
    }
    return value;
};
const getActionLabel = (action) => action === 'FlashRepay'
    ? 'Transaction'
    : ACTION_DISPLAY_NAMES[action] || action;
const getTransactionTitle = (status, action) => {
    if (status === 'awaiting')
        return 'Awaiting Response';
    if (status === 'insufficient_gas_balance')
        return 'Insufficient Gas Balance';
    if (status === 'cancelled')
        return 'Transaction Cancelled';
    const label = action ? getActionLabel(action) : 'Transaction';
    if (status === 'success')
        return `${label} Successful`;
    if (status === 'error')
        return action === 'FlashRepay' ? 'Flash Repay Failed' : `${label} Failed`;
    return 'Transaction Failed';
};
const getTransactionDescription = (status, action, displayAmount, tokenSymbol, errorMessage, swapParams, flashRepayContext) => {
    var _a;
    if (status === 'awaiting') {
        if (action === 'FlashRepay' && flashRepayContext) {
            const { repaySymbol, withdrawSymbol, repayDisplayAmount, isPartial } = flashRepayContext;
            return isPartial && repayDisplayAmount
                ? `Repaying ${repayDisplayAmount} ${repaySymbol} using ${withdrawSymbol} collateral. Approve in your wallet.`
                : `Repaying ${repaySymbol} using ${withdrawSymbol} collateral. Approve in your wallet.`;
        }
        return 'Requesting wallet to approve transaction.';
    }
    if (status === 'insufficient_gas_balance')
        return `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`;
    if (status === 'cancelled')
        return `${action === 'FlashRepay' ? 'Flash Repay' : action} transaction was cancelled.`;
    if (status === 'success' && flashRepayContext) {
        const { repaySymbol, withdrawSymbol, repayDisplayAmount, isPartial } = flashRepayContext;
        return isPartial && repayDisplayAmount
            ? `Repaid ${repayDisplayAmount} ${repaySymbol} using ${withdrawSymbol} from collateral.`
            : `Repaid ${repaySymbol} using ${withdrawSymbol} from collateral.`;
    }
    if (status === 'error' && action === 'FlashRepay') {
        if (flashRepayContext && errorMessage)
            return `Flash repay (${flashRepayContext.repaySymbol} via ${flashRepayContext.withdrawSymbol}) failed. ${errorMessage}`;
        return errorMessage ? `Flash repay failed. ${errorMessage}` : 'Flash repay failed.';
    }
    if (status === 'success' && action === 'swap_supply' && swapParams) {
        return `Successfully supplied ${swapParams.receivedAmount || displayAmount} ${tokenSymbol} by swapping ${swapParams.swappedFromAmount} ${swapParams.swappedFromTokenSymbol}.`;
    }
    if (status === 'success' && action === 'swap_repay' && swapParams) {
        const fmt = (v, fallback) => parseFloat(v || '0') > 0.0001
            ? parseFloat(v || '0').toFixed(4)
            : formatSmallNumber(v || fallback || '');
        return `Successfully repaid ${fmt(swapParams.receivedAmount || '', displayAmount)} ${tokenSymbol} by swapping ${fmt(swapParams.swappedFromAmount)} ${swapParams.swappedFromTokenSymbol}.`;
    }
    const actionVerb = {
        Supply: 'supplied',
        Withdraw: 'withdrawn',
        Borrow: 'borrowed',
        Repay: 'repaid',
        swap_supply: 'swapped & supplied',
        swap_repay: 'swapped & repaid',
        FlashRepay: 'closed position via flash repay',
    };
    if (status === 'success' && action && displayAmount && tokenSymbol)
        return `You have successfully ${actionVerb[action]} ${displayAmount} ${tokenSymbol}.`;
    if (status === 'error' && action) {
        const amount = displayAmount && parseFloat(displayAmount) < 0.0001
            ? formatSmallNumber(displayAmount)
            : displayAmount;
        const actionName = ((_a = ACTION_DISPLAY_NAMES[action]) === null || _a === void 0 ? void 0 : _a.toLowerCase()) ||
            action.toLowerCase();
        return `Failed to ${actionName} ${amount || ''} ${tokenSymbol || ''}. ${errorMessage || ''}`;
    }
    return errorMessage || 'Transaction failed';
};
const createNotificationInfo = (network, status, params = {}) => {
    const { result, action, token, displayAmount, errorMessage, swapParams, flashRepayContext } = params;
    const explorerLink = createExplorerLink(network, result === null || result === void 0 ? void 0 : result.digest);
    const describe = (s) => getTransactionDescription(s, action, displayAmount, token === null || token === void 0 ? void 0 : token.symbol, errorMessage, swapParams, flashRepayContext);
    const configs = {
        awaiting: {
            title: getTransactionTitle('awaiting'),
            description: describe('awaiting'),
            spinner: true,
        },
        success: {
            title: getTransactionTitle('success', action),
            description: describe('success'),
            image: getIcon('success'),
            link: explorerLink,
        },
        error: {
            title: getTransactionTitle('error', action),
            description: describe('error'),
            image: getIcon('failed'),
            link: explorerLink,
        },
        cancelled: {
            title: getTransactionTitle('cancelled', action),
            description: describe('cancelled'),
            image: getIcon('failed'),
        },
        insufficient_gas_balance: {
            title: getTransactionTitle('insufficient_gas_balance'),
            description: describe('insufficient_gas_balance'),
            image: getIcon('failed'),
        },
    };
    return configs[status];
};
/** Shared notification factory for both claim rewards handlers. */
const createClaimNotification = (status, network, errorMessage, result) => {
    const explorerLink = createExplorerLink(network, result === null || result === void 0 ? void 0 : result.digest);
    const friendlyError = errorMessage
        ? getMoveErrorMessage(errorMessage)
        : 'Failed to claim rewards.';
    const configs = {
        awaiting: {
            title: 'Awaiting Response',
            description: 'Requesting wallet to approve transaction.',
            spinner: true,
        },
        success: {
            title: 'Claim Rewards Successful',
            description: 'You have successfully claimed your rewards.',
            image: getIcon('success'),
            link: explorerLink,
        },
        error: {
            title: 'Claim Rewards Failed',
            description: friendlyError,
            image: getIcon('failed'),
            link: explorerLink,
        },
        cancelled: {
            title: 'Transaction Cancelled',
            description: 'Claim rewards transaction was cancelled.',
            image: getIcon('failed'),
        },
        insufficient_gas_balance: {
            title: 'Insufficient Gas Balance',
            description: `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`,
            image: getIcon('failed'),
        },
    };
    return configs[status];
};
const submitTransaction = (transactionBlock, suiClient, signAndExecuteTransaction, setIsTransactionLoading, callbacks) => {
    // Admin dry-run mode: the dry run already ran and was reported to the debug
    // panel; never prompt a signature for an impersonated address.
    if (dryRunDebug.isEnabled()) {
        setIsTransactionLoading(false);
        return;
    }
    signAndExecuteTransaction({ transaction: transactionBlock }, {
        onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
            try {
                // `result.success` is authoritative — dApp Kit resolves only after the wallet
                // executed the tx and parsed its effects. Show the outcome immediately; do NOT
                // block the success notification on a second waitForTransaction round-trip (that
                // polls the fullnode for up to 60s and would leave the modal stuck on "awaiting").
                if (result.success) {
                    yield callbacks.onSuccess(result);
                }
                else {
                    // Genuine on-chain revert (rare) — fetch effects to surface the specific error.
                    const txbCheck = yield suiClient
                        .waitForTransaction({ digest: result.digest, include: { effects: true } })
                        .catch(() => null);
                    const msg = (txbCheck && getTxErrorMessage(txbCheck)) || 'Transaction failed';
                    callbacks.onChainError(msg, result);
                }
            }
            catch (_a) {
                // Only reached if callbacks.onSuccess itself throws.
                callbacks.onConfirmError(result);
            }
            finally {
                setIsTransactionLoading(false);
            }
        }),
        onError: callbacks.onWalletError,
    });
};
export const alphalendTransactionHandler = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { amount, network, action, modalState, address, positionCapId, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setIsTransactionLoading, setIsUserRejectionError, displayAmount, suiBalance, resetInputValues, notifyTransaction, suppliedAndBorrowedCoinTypes = [], path, borrowedCoinTypes = [], } = params;
    const showNotification = (status, notifParams = {}) => {
        setNotificationInfo(createNotificationInfo(network, status, notifParams));
        setIsNotificationOpen(true);
    };
    const handleError = (rawError, shouldResetInputs = true, result, userRejected = false, 
    // Stands in for the *message* when the caught value carries none, so callers can forward
    // the value itself unconditionally — a rejection payload with a `code` and no `message`
    // still reaches the log, where a stand-in string would have replaced it.
    fallbackMessage = '') => {
        var _a, _b;
        const displayError = getMoveErrorMessage(getRawErrorMessage(rawError) || fallbackMessage);
        logTransactionError(action, rawError, displayError);
        showNotification('error', {
            errorMessage: displayError,
            action,
            token: modalState.token,
            displayAmount,
            result,
        });
        setIsTransactionLoading(false);
        if (shouldResetInputs)
            resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
        dispatchTransactionEvent(action.toLowerCase(), false, {
            $Market: (_a = modalState.token) === null || _a === void 0 ? void 0 : _a.symbol,
            $MarketID: (_b = modalState.token) === null || _b === void 0 ? void 0 : _b.marketId,
            $Amount: displayAmount,
            $ErrorMessage: displayError,
            $UserRejected: userRejected,
            $Path: path !== null && path !== void 0 ? path : '',
        });
    };
    if (!address || !modalState.token) {
        console.error('Address and token are required for alphalend transactions');
        return;
    }
    if (action !== 'Supply' && !positionCapId) {
        console.error('Position Cap ID is required for', action);
        showNotification('error', {
            action,
            token: modalState.token,
            displayAmount,
            errorMessage: 'Position Cap ID is required. Please connect your wallet.',
        });
        return;
    }
    if (hasInsufficientBalance(suiBalance)) {
        showNotification('insufficient_gas_balance');
        return;
    }
    const { suiClient, alphalendClient } = createClients(network);
    try {
        const marketId = modalState.token.marketId;
        const marketInfo = marketsArray.find((m) => m.id === marketId);
        if (!marketInfo)
            throw new Error(`Market info not found for market ID: ${marketId}`);
        const { coinType, expo } = marketInfo;
        const amountBigInt = BigInt((amount === '18446744073709551615'
            ? new BigNumber(amount)
            : new BigNumber(amount).multipliedBy(new BigNumber(10).pow(expo))).toFixed(0));
        const txBase = {
            marketId,
            amount: amountBigInt,
            coinType,
            positionCapId: positionCapId || undefined,
            address,
        };
        const priceUpdateCoinTypes = suppliedAndBorrowedCoinTypes.length > 0 ? suppliedAndBorrowedCoinTypes : [coinType];
        setIsTransactionLoading(true);
        let transactionBlock;
        switch (action) {
            case 'Supply':
                transactionBlock = yield alphalendClient.supply(txBase);
                break;
            case 'Withdraw':
                transactionBlock = yield alphalendClient.withdraw(Object.assign(Object.assign({}, txBase), { positionCapId: positionCapId, priceUpdateCoinTypes: borrowedCoinTypes.length > 0 ? priceUpdateCoinTypes : [] }));
                break;
            case 'Borrow':
                transactionBlock = yield alphalendClient.borrow(Object.assign(Object.assign({}, txBase), { positionCapId: positionCapId, priceUpdateCoinTypes }));
                break;
            case 'Repay':
                transactionBlock = yield alphalendClient.repay(Object.assign(Object.assign({}, txBase), { positionCapId: positionCapId }));
                break;
            default:
                throw new Error(`Unsupported action: ${action}`);
        }
        if (!transactionBlock) {
            console.error('Transaction block is null/undefined after creation');
            throw new Error('Failed to create transaction block');
        }
        // Show "awaiting" immediately for instant feedback, before the async dry-run
        // simulation runs (previously it only appeared after the dry-run resolved, which
        // felt like the notification opened late). Admin dry-run mode closes it after
        // simulating and lets the DryRunResultBox present the outcome instead.
        showNotification('awaiting');
        // Pin before the dry run so the simulation runs under the budget the wallet will use.
        transactionBlock.setGasBudget(GAS_BUDGET_MIST);
        const dryRun = yield dryRunAndReport(transactionBlock, address, suiClient, action);
        if (dryRunDebug.isEnabled()) {
            setIsNotificationOpen(false);
            setIsTransactionLoading(false);
            return;
        }
        if (!dryRun.success) {
            handleError(dryRun.rawError);
            return;
        }
        submitTransaction(transactionBlock, suiClient, signAndExecuteTransaction, setIsTransactionLoading, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                showNotification('success', { result, action, token: modalState.token, displayAmount });
                resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
                notifyTransaction === null || notifyTransaction === void 0 ? void 0 : notifyTransaction(marketId, positionCapId, action.toLowerCase());
                dispatchTransactionEvent(action.toLowerCase(), true, {
                    $Market: (_a = modalState.token) === null || _a === void 0 ? void 0 : _a.symbol,
                    $MarketID: marketId,
                    $Amount: displayAmount,
                    $Path: path !== null && path !== void 0 ? path : '',
                    digest: result.digest,
                });
            }),
            onChainError: (msg, result) => handleError(msg, true, result),
            onConfirmError: (result) => handleError('Failed to confirm transaction', false, result),
            onWalletError: (error) => {
                const isUserRejection = isUserRejectionError(error);
                setIsUserRejectionError === null || setIsUserRejectionError === void 0 ? void 0 : setIsUserRejectionError(isUserRejection);
                // Hand over the error itself, not its message, so handleError's log keeps the stack.
                handleError(error, !isUserRejection, undefined, isUserRejection, 'Transaction cancelled');
            },
        });
    }
    catch (error) {
        handleError(error);
    }
});
export const alphalendSwapAndSupplyOrRepayTransactionHandler = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { amount, network, action, modalState, address, positionCapId, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setIsTransactionLoading, displayAmount, receivedAmountDisplay, suiBalance, swappedFromTokenCoinType, swappedFromTokenSymbol, swappedIntoTokenCoinType, slippage, resetInputValues, notifyTransaction, path, } = params;
    const showNotification = (status, notifParams = {}) => {
        setNotificationInfo(createNotificationInfo(network, status, notifParams));
        setIsNotificationOpen(true);
    };
    const handleError = (rawError, shouldResetInputs = true, result, userRejected = false, 
    // Stands in for the *message* when the caught value carries none, so callers can forward
    // the value itself unconditionally and keep its payload in the log.
    fallbackMessage = '') => {
        var _a, _b;
        const displayError = getMoveErrorMessage(getRawErrorMessage(rawError) || fallbackMessage);
        logTransactionError(action, rawError, displayError);
        showNotification('error', {
            errorMessage: displayError,
            action,
            token: modalState.token,
            displayAmount,
            result,
        });
        setIsTransactionLoading(false);
        if (shouldResetInputs)
            resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
        dispatchTransactionEvent(action.toLowerCase(), false, {
            $Market: (_a = modalState.token) === null || _a === void 0 ? void 0 : _a.symbol,
            $MarketID: (_b = modalState.token) === null || _b === void 0 ? void 0 : _b.marketId,
            $Amount: displayAmount,
            $SwapFromToken: swappedFromTokenSymbol,
            $ErrorMessage: displayError,
            $UserRejected: userRejected,
            $Path: path !== null && path !== void 0 ? path : '',
        });
    };
    if (!address || !modalState.token) {
        console.error('Address and token are required for alphalend transactions');
        return;
    }
    if (action !== 'Supply' && !positionCapId) {
        console.error('Position Cap ID is required for', action);
        showNotification('error', {
            action,
            token: modalState.token,
            displayAmount,
            errorMessage: 'Position Cap ID is required. Please connect your wallet.',
        });
        return;
    }
    if (hasInsufficientBalance(suiBalance)) {
        showNotification('insufficient_gas_balance');
        return;
    }
    const { suiClient, alphalendClient } = createClients(network);
    try {
        const marketId = modalState.token.marketId;
        const marketInfo = marketsArray.find((m) => m.id === marketId);
        if (!marketInfo)
            throw new Error(`Market info not found for market ID: ${marketId}`);
        const amountBigInt = BigInt(new BigNumber(amount).toFixed(0));
        const slippageDecimal = slippage !== undefined ? slippage / 100 : 0.005;
        setIsTransactionLoading(true);
        let transactionBlock;
        switch (action) {
            case 'swap_supply':
                transactionBlock = yield alphalendClient.zapInSupply({
                    marketId,
                    slippage: slippageDecimal,
                    marketCoinType: swappedIntoTokenCoinType,
                    inputAmount: amountBigInt,
                    inputCoinType: swappedFromTokenCoinType,
                    positionCapId: positionCapId,
                    address,
                });
                break;
            case 'swap_repay':
                transactionBlock = yield alphalendClient.swapAndRepay({
                    marketId,
                    amount: amountBigInt,
                    swapFromCoinType: swappedFromTokenCoinType,
                    positionCapId: positionCapId,
                    address,
                    slippage: slippageDecimal,
                    swapToCoinType: swappedIntoTokenCoinType,
                });
                break;
            default:
                throw new Error(`Unsupported action: ${action}`);
        }
        if (!transactionBlock)
            throw new Error('Failed to create transaction block');
        // Show "awaiting" immediately for instant feedback, before the async dry-run
        // simulation runs (previously it only appeared after the dry-run resolved, which
        // felt like the notification opened late). Admin dry-run mode closes it after
        // simulating and lets the DryRunResultBox present the outcome instead.
        showNotification('awaiting');
        // Pin before the dry run so the simulation runs under the budget the wallet will use.
        transactionBlock.setGasBudget(GAS_BUDGET_MIST);
        const dryRun = yield dryRunAndReport(transactionBlock, address, suiClient, action);
        if (dryRunDebug.isEnabled()) {
            setIsNotificationOpen(false);
            setIsTransactionLoading(false);
            return;
        }
        if (!dryRun.success) {
            handleError(dryRun.rawError);
            return;
        }
        submitTransaction(transactionBlock, suiClient, signAndExecuteTransaction, setIsTransactionLoading, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                var _a, _b;
                let tokenSymbol = swappedFromTokenSymbol || '';
                if (!tokenSymbol) {
                    const swappedFromMarket = marketsArray.find((m) => m.coinType === swappedFromTokenCoinType);
                    tokenSymbol = (swappedFromMarket === null || swappedFromMarket === void 0 ? void 0 : swappedFromMarket.symbol) || '';
                }
                if (!tokenSymbol && swappedFromTokenCoinType) {
                    const parts = swappedFromTokenCoinType.split('::');
                    tokenSymbol = ((_a = parts[parts.length - 1]) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || '';
                }
                const receivedAmountToDisplay = receivedAmountDisplay ||
                    new BigNumber(amount).dividedBy(new BigNumber(10).pow(marketInfo.expo)).toFixed(4);
                const swapParams = (action === 'swap_supply' || action === 'swap_repay') && displayAmount && tokenSymbol
                    ? {
                        swappedFromAmount: displayAmount,
                        swappedFromTokenSymbol: tokenSymbol,
                        receivedAmount: receivedAmountToDisplay,
                    }
                    : undefined;
                showNotification('success', {
                    result,
                    action,
                    token: modalState.token,
                    displayAmount: receivedAmountToDisplay,
                    swapParams,
                });
                resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
                notifyTransaction === null || notifyTransaction === void 0 ? void 0 : notifyTransaction(marketId, positionCapId, action.toLowerCase());
                dispatchTransactionEvent(action.toLowerCase(), true, {
                    $Market: (_b = modalState.token) === null || _b === void 0 ? void 0 : _b.symbol,
                    $MarketID: marketId,
                    $Amount: receivedAmountToDisplay,
                    $SwapFromToken: tokenSymbol,
                    $Path: path !== null && path !== void 0 ? path : '',
                    digest: result.digest,
                });
            }),
            onChainError: (msg, result) => handleError(msg, true, result),
            onConfirmError: (result) => handleError('Failed to confirm transaction', false, result),
            onWalletError: (error) => {
                const isRejection = isUserRejectionError(error);
                handleError(error, !isRejection, undefined, isRejection, 'Transaction cancelled');
            },
        });
    }
    catch (error) {
        handleError(error);
    }
});
export const alphalendFlashRepayTransactionHandler = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { positionCapId, repayCoinType, repayMarketId, withdrawCoinType, withdrawMarketId, address, slippage, network, repayAmountBaseUnits, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setIsTransactionLoading, resetInputValues, notifyTransaction, notificationContext, } = params;
    const flashRepayContext = notificationContext
        ? Object.assign({}, notificationContext) : undefined;
    const showNotification = (status, notifParams = {}) => {
        setNotificationInfo(createNotificationInfo(network, status, Object.assign(Object.assign({}, notifParams), { flashRepayContext })));
        setIsNotificationOpen(true);
    };
    const handleError = (rawError, result, userRejected = false, 
    // Stands in for the *message* when the caught value carries none, so callers can forward
    // the value itself unconditionally and keep its payload in the log.
    fallbackMessage = '') => {
        var _a;
        const displayError = getMoveErrorMessage(getRawErrorMessage(rawError) || fallbackMessage);
        logTransactionError('FlashRepay', rawError, displayError);
        showNotification('error', {
            errorMessage: displayError,
            action: 'FlashRepay',
            result,
        });
        setIsTransactionLoading(false);
        dispatchTransactionEvent('flash_repay', false, {
            $Market: flashRepayContext === null || flashRepayContext === void 0 ? void 0 : flashRepayContext.repaySymbol,
            $Amount: (_a = flashRepayContext === null || flashRepayContext === void 0 ? void 0 : flashRepayContext.repayDisplayAmount) !== null && _a !== void 0 ? _a : '',
            $ErrorMessage: displayError,
            $UserRejected: userRejected,
        });
    };
    if (!address || !positionCapId) {
        handleError('Address and position are required.');
        return;
    }
    const { suiClient, alphalendClient } = createClients(network);
    try {
        setIsTransactionLoading(true);
        const transactionBlock = yield alphalendClient.flashRepay(Object.assign({ positionCapId,
            repayCoinType,
            repayMarketId,
            withdrawCoinType,
            withdrawMarketId,
            address,
            slippage }, (repayAmountBaseUnits != null && { repayAmountBaseUnits })));
        if (!transactionBlock) {
            handleError('Failed to build flash repay transaction.');
            return;
        }
        // Admin dry-run mode: show "awaiting" while simulating, then let the
        // DryRunResultBox present the outcome. Never sign for an impersonated address.
        if (dryRunDebug.isEnabled())
            showNotification('awaiting', { action: 'FlashRepay' });
        // Pin before the dry run so the simulation runs under the budget the wallet will use.
        transactionBlock.setGasBudget(GAS_BUDGET_MIST);
        const dryRun = yield dryRunAndReport(transactionBlock, address, suiClient, 'FlashRepay');
        if (dryRunDebug.isEnabled()) {
            setIsNotificationOpen(false);
            setIsTransactionLoading(false);
            return;
        }
        if (!dryRun.success) {
            handleError(dryRun.rawError);
            return;
        }
        showNotification('awaiting', { action: 'FlashRepay' });
        submitTransaction(transactionBlock, suiClient, signAndExecuteTransaction, setIsTransactionLoading, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                showNotification('success', { action: 'FlashRepay', result });
                notifyTransaction === null || notifyTransaction === void 0 ? void 0 : notifyTransaction(repayMarketId, positionCapId, 'repay');
                dispatchTransactionEvent('flash_repay', true, {
                    $Market: flashRepayContext === null || flashRepayContext === void 0 ? void 0 : flashRepayContext.repaySymbol,
                    $Amount: (_a = flashRepayContext === null || flashRepayContext === void 0 ? void 0 : flashRepayContext.repayDisplayAmount) !== null && _a !== void 0 ? _a : '',
                    digest: result.digest,
                });
                resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
            }),
            onChainError: (msg, result) => handleError(msg, result),
            onConfirmError: (result) => handleError('Failed to confirm transaction', result),
            onWalletError: (error) => {
                const isRejection = isUserRejectionError(error);
                handleError(error, undefined, isRejection, 'Transaction cancelled');
            },
        });
    }
    catch (error) {
        handleError(error);
    }
});
export const claimRewardsTransactionHandler = (params) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const { address, positionCapId, network, notifyTransaction, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setIsTransactionLoading, claimAll, claimAlpha, suiBalance, resetInputValues, rewardsAnalytics, } = params;
    const showNotification = (status, errorMessage, result) => {
        setNotificationInfo(createClaimNotification(status, network, errorMessage, result));
        setIsNotificationOpen(true);
    };
    if (!address || !positionCapId) {
        console.error('Address and Position Cap ID are required for claiming rewards');
        return;
    }
    if (hasInsufficientBalance(suiBalance, true)) {
        showNotification('insufficient_gas_balance');
        return;
    }
    const claimBasePayload = {
        $TotalRewards: (_a = rewardsAnalytics === null || rewardsAnalytics === void 0 ? void 0 : rewardsAnalytics.totalUsd) !== null && _a !== void 0 ? _a : '',
        $Rewards: (_b = rewardsAnalytics === null || rewardsAnalytics === void 0 ? void 0 : rewardsAnalytics.rewards) !== null && _b !== void 0 ? _b : [],
        $SwapTo: false,
        $SwapToToken: null,
        '$Supply/RepayOnLend': false,
    };
    const { suiClient, alphalendClient } = createClients(network);
    try {
        setIsTransactionLoading(true);
        showNotification('awaiting');
        const transactionBlock = yield alphalendClient.claimRewards({
            positionCapId,
            address,
            claimAll,
            claimAlpha,
        });
        if (!transactionBlock)
            throw new Error('Failed to create transaction block');
        // Pin before the dry run so the simulation runs under the budget the wallet will use.
        transactionBlock.setGasBudget(GAS_BUDGET_MIST);
        const dryRun = yield dryRunAndReport(transactionBlock, address, suiClient, 'ClaimRewards');
        // Admin dry-run mode: awaiting was already shown above; close it and let the
        // DryRunResultBox present the outcome. Never sign for an impersonated address.
        if (dryRunDebug.isEnabled()) {
            setIsNotificationOpen(false);
            setIsTransactionLoading(false);
            return;
        }
        if (!dryRun.success) {
            logTransactionError('ClaimRewards', dryRun.rawError);
            // Pass rawError: createClaimNotification applies getMoveErrorMessage internally
            showNotification('error', dryRun.rawError);
            setIsTransactionLoading(false);
            resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
            dispatchTransactionEvent('claim_reward', false, Object.assign(Object.assign({}, claimBasePayload), { $UserRejected: false, $ErrorMessage: getMoveErrorMessage(dryRun.rawError) }));
            return;
        }
        submitTransaction(transactionBlock, suiClient, signAndExecuteTransaction, setIsTransactionLoading, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                showNotification('success', undefined, result);
                if (notifyTransaction && positionCapId) {
                    console.log('💰 [CLAIM_REWARD] Sending notification to WebSocket server for position:', positionCapId);
                    notifyTransaction(positionCapId);
                }
                dispatchTransactionEvent('claim_reward', true, Object.assign(Object.assign({}, claimBasePayload), { digest: result.digest }));
                resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
            }),
            onChainError: (msg, result) => {
                logTransactionError('ClaimRewards', msg);
                showNotification('error', msg, result);
                resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
                dispatchTransactionEvent('claim_reward', false, Object.assign(Object.assign({}, claimBasePayload), { $UserRejected: false, $ErrorMessage: getMoveErrorMessage(msg) }));
            },
            onConfirmError: (result) => {
                logTransactionError('ClaimRewards', 'Failed to confirm transaction');
                showNotification('error', 'Failed to confirm transaction', result);
                dispatchTransactionEvent('claim_reward', false, Object.assign(Object.assign({}, claimBasePayload), { $UserRejected: false, $ErrorMessage: 'Failed to confirm transaction' }));
            },
            onWalletError: (error) => {
                const userRejected = isUserRejectionError(error);
                // Pass rawError: createClaimNotification applies getMoveErrorMessage internally, so
                // mapping here too ran the message through the map twice. The second pass no longer
                // sees a Move abort, matches nothing, and falls back to the generic text — the toast
                // said "unexpected error" while the log claimed the user saw the specific message.
                const rawMessage = error.message ||
                    (userRejected ? 'You cancelled the claim rewards.' : 'Transaction failed.');
                const displayError = getMoveErrorMessage(rawMessage);
                logTransactionError('ClaimRewards', error, displayError);
                showNotification('error', rawMessage);
                setIsTransactionLoading(false);
                if (!userRejected)
                    resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
                dispatchTransactionEvent('claim_reward', false, Object.assign(Object.assign({}, claimBasePayload), { $UserRejected: userRejected, $ErrorMessage: displayError }));
            },
        });
    }
    catch (error) {
        // Pass rawError: createClaimNotification applies getMoveErrorMessage internally.
        const rawMessage = getRawErrorMessage(error) || 'Unexpected error occurred.';
        const displayError = getMoveErrorMessage(rawMessage);
        logTransactionError('ClaimRewards', error, displayError);
        showNotification('error', rawMessage);
        setIsTransactionLoading(false);
        resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
        dispatchTransactionEvent('claim_reward', false, Object.assign(Object.assign({}, claimBasePayload), { $UserRejected: false, $ErrorMessage: displayError }));
    }
});
export const advancedClaimRewardsTransactionHandler = (params) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const { address, positionCapId, network, notifyTransaction, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setIsTransactionLoading, swapToToken, supplyOrRepay, selectedToken, suiBalance, resetInputValues, suppliedAndBorrowedCoinTypes = [], borrowedAmounts, rewardsByToken, marketsArray: marketsData, rewardsAnalytics, } = params;
    const claimBasePayload = {
        $TotalRewards: (_a = rewardsAnalytics === null || rewardsAnalytics === void 0 ? void 0 : rewardsAnalytics.totalUsd) !== null && _a !== void 0 ? _a : '',
        $Rewards: (_b = rewardsAnalytics === null || rewardsAnalytics === void 0 ? void 0 : rewardsAnalytics.rewards) !== null && _b !== void 0 ? _b : [],
        $SwapTo: swapToToken,
        $SwapToToken: swapToToken ? (selectedToken !== null && selectedToken !== void 0 ? selectedToken : null) : null,
        '$Supply/RepayOnLend': supplyOrRepay,
    };
    const showNotification = (status, errorMessage, result) => {
        setNotificationInfo(createClaimNotification(status, network, errorMessage, result));
        setIsNotificationOpen(true);
    };
    if (!address || !positionCapId) {
        console.error('Address and Position Cap ID are required for claiming rewards');
        return;
    }
    if (hasInsufficientBalance(suiBalance, true)) {
        showNotification('insufficient_gas_balance');
        return;
    }
    // Calculate reward amounts in base units for accurate swap quotes
    let rewardAmounts;
    if (rewardsByToken && marketsData && marketsData.length > 0) {
        rewardAmounts = new Map();
        for (const [coinType, amount] of Object.entries(rewardsByToken)) {
            const rewardAmount = parseFloat(amount);
            if (rewardAmount <= 0)
                continue;
            const market = marketsData.find((m) => m.coinType.toLowerCase() === coinType.toLowerCase());
            if (!market)
                continue;
            rewardAmounts.set(coinType, Math.floor(rewardAmount * Math.pow(10, market.expo)).toString());
        }
    }
    const { suiClient, alphalendClient } = createClients(network);
    try {
        setIsTransactionLoading(true);
        showNotification('awaiting');
        let transactionBlock;
        const targetMarket = marketsData === null || marketsData === void 0 ? void 0 : marketsData.find((m) => m.symbol === selectedToken);
        const priceUpdateCoinTypes = suppliedAndBorrowedCoinTypes.length > 0 ? suppliedAndBorrowedCoinTypes : [];
        if (!swapToToken && !supplyOrRepay) {
            // Claim all to wallet
            transactionBlock = yield alphalendClient.claimRewards({
                positionCapId,
                address,
                claimAll: false,
                claimAlpha: false,
            });
        }
        else if (swapToToken && !supplyOrRepay) {
            // Swap tokens >= $0.01, claim tokens < $0.01 to wallet
            if (!selectedToken)
                throw new Error('Selected token is required for swap');
            const targetCoinType = SWAP_TOKEN_TO_COIN_TYPE[selectedToken];
            if (!targetCoinType)
                throw new Error(`Unknown token: ${selectedToken}. Cannot determine coin type for swap.`);
            transactionBlock = yield alphalendClient.claimSwapAndSupplyOrRepayOrTransfer({
                positionCapId,
                address,
                targetCoinType,
                slippage: 0.01,
                transferTargetCoin: true,
                priceUpdateCoinTypes,
                rewardAmounts,
                claimSmallRewardsToWallet: true,
            });
        }
        else if (!swapToToken && supplyOrRepay) {
            // Repay borrowed coins, supply the rest to markets
            const borrowedCoinsMap = new Map();
            const supplyMarketsMap = new Map();
            if ((borrowedAmounts === null || borrowedAmounts === void 0 ? void 0 : borrowedAmounts.size) && marketsData) {
                for (const [coinType] of borrowedAmounts.entries()) {
                    const market = marketsData.find((m) => m.coinType === coinType);
                    if (market) {
                        const marketInfo = { marketId: market.id };
                        borrowedCoinsMap.set(coinType, marketInfo);
                        supplyMarketsMap.set(coinType, marketInfo);
                    }
                }
            }
            transactionBlock = yield alphalendClient.claimAndSupplyOrRepay({
                positionCapId,
                address,
                borrowedCoins: borrowedCoinsMap,
                supplyMarkets: supplyMarketsMap,
                priceUpdateCoinTypes,
            });
        }
        else if (swapToToken && supplyOrRepay) {
            // Swap to selected token, then supply or repay
            if (!selectedToken)
                throw new Error('Selected token is required for swap');
            if (!targetMarket) {
                transactionBlock = yield alphalendClient.claimRewards({
                    positionCapId,
                    address,
                    claimAll: false,
                    claimAlpha: false,
                });
            }
            else {
                const isMarketAvailable = !targetMarket.retired && targetMarket.active !== false;
                const hasBorrowedTargetToken = borrowedAmounts === null || borrowedAmounts === void 0 ? void 0 : borrowedAmounts.has(targetMarket.coinType);
                if (!isMarketAvailable && !hasBorrowedTargetToken) {
                    transactionBlock = yield alphalendClient.claimSwapAndSupplyOrRepayOrTransfer({
                        positionCapId,
                        address,
                        targetCoinType: targetMarket.coinType,
                        slippage: 0.01,
                        transferTargetCoin: true,
                        priceUpdateCoinTypes,
                        rewardAmounts,
                        claimSmallRewardsToWallet: false,
                    });
                }
                else if (hasBorrowedTargetToken) {
                    transactionBlock = yield alphalendClient.claimSwapAndSupplyOrRepayOrTransfer({
                        positionCapId,
                        address,
                        targetCoinType: targetMarket.coinType,
                        targetMarketId: targetMarket.id,
                        slippage: 0.01,
                        isTargetBorrowed: true,
                        priceUpdateCoinTypes,
                        rewardAmounts,
                        claimSmallRewardsToWallet: false,
                    });
                }
                else {
                    transactionBlock = yield alphalendClient.claimSwapAndSupplyOrRepayOrTransfer({
                        positionCapId,
                        address,
                        targetCoinType: targetMarket.coinType,
                        targetMarketId: targetMarket.id,
                        slippage: 0.01,
                        priceUpdateCoinTypes: suppliedAndBorrowedCoinTypes.length > 0
                            ? suppliedAndBorrowedCoinTypes
                            : [targetMarket.coinType],
                        rewardAmounts,
                        claimSmallRewardsToWallet: false,
                    });
                }
            }
        }
        if (!transactionBlock)
            throw new Error('Failed to create transaction block');
        // Pin before the dry run so the simulation runs under the budget the wallet will use.
        transactionBlock.setGasBudget(GAS_BUDGET_MIST);
        const dryRun = yield dryRunAndReport(transactionBlock, address, suiClient, 'AdvancedClaimRewards');
        // Admin dry-run mode: awaiting was already shown above; close it and let the
        // DryRunResultBox present the outcome. Never sign for an impersonated address.
        if (dryRunDebug.isEnabled()) {
            setIsNotificationOpen(false);
            setIsTransactionLoading(false);
            return;
        }
        if (!dryRun.success) {
            logTransactionError('ClaimRewards', dryRun.rawError);
            // Pass rawError: createClaimNotification applies getMoveErrorMessage internally
            showNotification('error', dryRun.rawError);
            setIsTransactionLoading(false);
            resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
            dispatchTransactionEvent('claim_reward', false, Object.assign(Object.assign({}, claimBasePayload), { $UserRejected: false, $ErrorMessage: getMoveErrorMessage(dryRun.rawError) }));
            return;
        }
        submitTransaction(transactionBlock, suiClient, signAndExecuteTransaction, setIsTransactionLoading, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                showNotification('success', undefined, result);
                if (notifyTransaction && positionCapId) {
                    console.log('💰 [CLAIM_REWARD] Sending notification to WebSocket server for position:', positionCapId);
                    notifyTransaction(positionCapId);
                }
                dispatchTransactionEvent('claim_reward', true, Object.assign(Object.assign({}, claimBasePayload), { digest: result.digest }));
                resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
            }),
            onChainError: (msg, result) => {
                logTransactionError('ClaimRewards', msg);
                showNotification('error', msg, result);
                resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
                dispatchTransactionEvent('claim_reward', false, Object.assign(Object.assign({}, claimBasePayload), { $UserRejected: false, $ErrorMessage: getMoveErrorMessage(msg) }));
            },
            onConfirmError: (result) => {
                logTransactionError('ClaimRewards', 'Failed to confirm transaction');
                showNotification('error', 'Failed to confirm transaction', result);
                dispatchTransactionEvent('claim_reward', false, Object.assign(Object.assign({}, claimBasePayload), { $UserRejected: false, $ErrorMessage: 'Failed to confirm transaction' }));
            },
            onWalletError: (error) => {
                const userRejected = isUserRejectionError(error);
                // Pass rawError: createClaimNotification applies getMoveErrorMessage internally, so
                // mapping here too ran the message through the map twice. The second pass no longer
                // sees a Move abort, matches nothing, and falls back to the generic text — the toast
                // said "unexpected error" while the log claimed the user saw the specific message.
                const rawMessage = error.message ||
                    (userRejected ? 'You cancelled the claim rewards.' : 'Transaction failed.');
                const displayError = getMoveErrorMessage(rawMessage);
                logTransactionError('ClaimRewards', error, displayError);
                showNotification('error', rawMessage);
                setIsTransactionLoading(false);
                if (!userRejected)
                    resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
                dispatchTransactionEvent('claim_reward', false, Object.assign(Object.assign({}, claimBasePayload), { $UserRejected: userRejected, $ErrorMessage: displayError }));
            },
        });
    }
    catch (error) {
        // Pass rawError: createClaimNotification applies getMoveErrorMessage internally.
        const rawMessage = getRawErrorMessage(error) || 'Unexpected error occurred.';
        const displayError = getMoveErrorMessage(rawMessage);
        logTransactionError('ClaimRewards', error, displayError);
        showNotification('error', rawMessage);
        setIsTransactionLoading(false);
        resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
        dispatchTransactionEvent('claim_reward', false, Object.assign(Object.assign({}, claimBasePayload), { $UserRejected: false, $ErrorMessage: displayError }));
    }
});
